import type { WorkflowDefinitionDocument } from "./platform-workflow-definition-lifecycle.models";
import {
  WORKFLOW_READ_ONLY_GRAPH_EDGE_LABEL_DIMENSIONS,
  WORKFLOW_READ_ONLY_GRAPH_NODE_DIMENSIONS,
  WORKFLOW_READ_ONLY_GRAPH_PLACEHOLDER_NODE_DIMENSIONS,
  WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_REQUIREMENTS_LANE_ID,
  WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_STAGES_LANE_ID,
  type WorkflowReadOnlyGraph,
  type WorkflowReadOnlyGraphDecisionGate,
  type WorkflowReadOnlyGraphDecisionGateOrigin,
  type WorkflowReadOnlyGraphEdge,
  type WorkflowReadOnlyGraphElementDetail,
  type WorkflowReadOnlyGraphFact,
  type WorkflowReadOnlyGraphNode,
  type WorkflowReadOnlyGraphNodeKind,
  type WorkflowReadOnlyGraphResponsibilityLane,
  type WorkflowReadOnlyGraphResponsibilityLaneKind,
} from "./workflow-read-only-graph.models";

interface IndexedValue {
  readonly index: number;
  readonly value: unknown;
}

interface CountResult {
  readonly applied: number;
  readonly malformed: number;
}

interface TransitionCountResult extends CountResult {
  readonly decisions: CountResult;
}

interface ProjectedNodeRecord {
  readonly index: number;
  readonly sourceId: string;
  readonly typeCode: string;
  readonly displayOrder: number | null;
  readonly stepCode: string | null;
  readonly displayName: string | null;
  readonly conditionCode: string | null;
  readonly conditionVersion: number | null;
  readonly transitions: TransitionCountResult;
  readonly directDecisions: CountResult;
  readonly decisionOrigins: readonly ProjectedDecisionOrigin[];
  readonly hasMalformedShape: boolean;
}

interface ProjectedEdgeRecord {
  readonly index: number;
  readonly sourceId: string;
  readonly fromSourceNodeId: string;
  readonly toSourceNodeId: string;
  readonly outcomeCode: string | null;
  readonly pathKindCode: string | null;
  readonly hasMalformedShape: boolean;
}

interface EndpointResolution {
  readonly visualizationId: string;
  readonly displayValue: string;
  readonly isInvalid: boolean;
  readonly status: string;
}

interface LinearChainProjection {
  readonly nodeProcessOrderBySourceIndex: ReadonlyMap<number, number>;
  readonly edgeOrderBySourceIndex: ReadonlyMap<number, number>;
}

interface DecisionApplication {
  readonly kind: "all" | "transition";
  readonly transitionIndex: number | null;
  readonly fromStateCode: string | null;
  readonly toStateCode: string | null;
  readonly isMalformed: boolean;
}

interface ProjectedDecisionOrigin {
  readonly id: string;
  readonly ordinal: number;
  readonly decisionIndex: number | null;
  readonly decisionKindCode: string | null;
  readonly requiredOutcomeCode: string | null;
  readonly reactionCode: string | null;
  readonly targetStateCode: string | null;
  readonly sourceKind: "structured" | "legacy";
  readonly ownerQueueCode: string | null;
  readonly eligibleRoleCodes: readonly string[];
  readonly notificationCount: number;
  readonly application: DecisionApplication;
  readonly isMalformed: boolean;
}

interface ProjectedDecisionGroup {
  readonly key: string;
  readonly decisionKindCode: string | null;
  readonly requiredOutcomeCode: string | null;
  readonly reactionCode: string | null;
  readonly targetStateCode: string | null;
  readonly origins: readonly ProjectedDecisionOrigin[];
  readonly isInvalid: boolean;
}

interface ProjectedDecisionGuidance {
  readonly laneId: string;
  readonly laneKind: WorkflowReadOnlyGraphResponsibilityLaneKind;
  readonly queueCodes: readonly string[];
  readonly roleCodes: readonly string[];
}

interface ProjectedDecisionScope {
  readonly kind: WorkflowReadOnlyGraphDecisionGate["scopeKind"];
  readonly label: string;
}

export type WorkflowGraphProjectionSourceContext = "working-draft" | "published-version";

export interface WorkflowGraphProjectionOptions {
  readonly sourceContext?: WorkflowGraphProjectionSourceContext;
}

const NODE_KIND_ORDER: Readonly<Record<WorkflowReadOnlyGraphNodeKind, number>> = {
  "automated-step": 1.5,
  entry: 0,
  "human-step": 1,
  "routing-decision": 1,
  terminal: 2,
  invalid: 3,
  "missing-endpoint": 4,
};

const TARGET_BEARING_REACTION_CODES = new Set(["branch", "auto_advance"]);
const SUPPORTED_REACTION_CODES = new Set(["permit", "block", ...TARGET_BEARING_REACTION_CODES]);
const NOTIFICATION_TARGET_REQUIRED_RECIPIENT_TYPES = new Set([
  "queue",
  "role",
  "manager",
  "external_destination",
]);
const NOTIFICATION_DERIVED_TARGET_RECIPIENT_TYPES = new Set([
  "current_assignee",
  "current_queue",
]);

export function projectWorkflowDraftGraph(
  document: WorkflowDefinitionDocument | null | undefined,
  options: WorkflowGraphProjectionOptions = {},
): WorkflowReadOnlyGraph {
  const sourceContext = options.sourceContext ?? "working-draft";
  const documentValue = asRecord(document);
  const nodeCollection = documentValue?.["nodes"];
  const edgeCollection = documentValue?.["edges"];
  const rawNodes = indexedArray(nodeCollection);
  const rawEdges = indexedArray(edgeCollection);

  const authoredNodeRecords = rawNodes.map(toNodeRecord);
  const duplicateNodeIds = findDuplicates(
    authoredNodeRecords
      .map((node) => node.sourceId)
      .filter(hasVisibleValue),
  );
  const authoredEdgeRecords = rawEdges.map(toEdgeRecord);
  const duplicateEdgeIds = findDuplicates(
    authoredEdgeRecords
      .map((edge) => edge.sourceId)
      .filter(hasVisibleValue),
  );
  const linearChain = projectLinearChain(
    authoredNodeRecords,
    authoredEdgeRecords,
    duplicateNodeIds,
    duplicateEdgeIds,
  );
  const nodeRecords = [...authoredNodeRecords].sort(compareNodeRecords);
  const nodeIdOccurrences = new Map<string, number>();
  const authoredNodes = nodeRecords.map((node) => {
    const occurrence = nextOccurrence(nodeIdOccurrences, node.sourceId);
    return toGraphNode(
      node,
      occurrence,
      duplicateNodeIds.has(node.sourceId),
      linearChain?.nodeProcessOrderBySourceIndex.get(node.index) ?? null,
      sourceContext,
    );
  });

  const nodesBySourceId = new Map<string, WorkflowReadOnlyGraphNode[]>();
  authoredNodes.forEach((node) => {
    if (!hasVisibleValue(node.sourceId)) {
      return;
    }

    const matches = nodesBySourceId.get(node.sourceId) ?? [];
    matches.push(node);
    nodesBySourceId.set(node.sourceId, matches);
  });

  const edgeRecords = [...authoredEdgeRecords].sort((left, right) =>
    compareProjectedEdges(left, right, linearChain));
  const placeholderNodes = new Map<string, WorkflowReadOnlyGraphNode>();
  const edgeIdOccurrences = new Map<string, number>();
  const edges = edgeRecords.map((edge) => {
    const occurrence = nextOccurrence(edgeIdOccurrences, edge.sourceId);
    const from = resolveEndpoint(
      edge.fromSourceNodeId,
      "from",
      nodesBySourceId,
      placeholderNodes,
      sourceContext,
    );
    const to = resolveEndpoint(
      edge.toSourceNodeId,
      "to",
      nodesBySourceId,
      placeholderNodes,
      sourceContext,
    );

    return toGraphEdge(
      edge,
      occurrence,
      from,
      to,
      duplicateEdgeIds.has(edge.sourceId),
    );
  });

  const sortedPlaceholders = [...placeholderNodes.values()].sort(compareGraphNodes);
  const nodes = [...authoredNodes, ...sortedPlaceholders];
  const workflowTransitions = countTransitions(documentValue?.["workflowTransitions"]);
  const workflowDecisions = countDecisions(documentValue?.["requiredDecisions"]);
  const workflowCode = readString(documentValue?.["workflowCode"]);
  const workflowDecisionGates = toDecisionGates(
    collectDecisionOrigins(
      documentValue?.["requiredDecisions"],
      documentValue?.["workflowTransitions"],
    ),
    "Whole workflow",
    workflowCode,
    null,
  );
  const workflowDecisionGateCount = workflowDecisionGates.filter(
    (gate) => !gate.isInvalid,
  ).length;
  const responsibilityLanes = toResponsibilityLanes(nodes);
  const appliedTransitionCount = workflowTransitions.applied
    + authoredNodes.reduce((total, node) => total + node.appliedTransitionCount, 0);
  const appliedDecisionCount = workflowDecisions.applied
    + workflowTransitions.decisions.applied
    + authoredNodes.reduce((total, node) => total + node.appliedDecisionCount, 0);
  const malformedEntryCount = collectionShapeErrorCount(nodeCollection)
    + collectionShapeErrorCount(edgeCollection)
    + collectionShapeErrorCount(documentValue?.["workflowTransitions"])
    + collectionShapeErrorCount(documentValue?.["requiredDecisions"])
    + workflowTransitions.malformed
    + workflowTransitions.decisions.malformed
    + workflowDecisions.malformed
    + authoredNodes.reduce(
      (total, node) => total + node.malformedTransitionCount + node.malformedDecisionCount,
      0,
    );
  const invalidElementCount = nodes.filter((node) => node.isInvalid).length
    + edges.filter((edge) => edge.isInvalid).length;
  const definitionVersionCode = readString(documentValue?.["definitionVersionCode"]);
  const workflowLabel = formatCodeLabel(workflowCode)
    || (sourceContext === "published-version" ? "Immutable workflow" : "Workflow draft");
  const versionLabel = hasVisibleValue(definitionVersionCode)
    ? ` version ${definitionVersionCode}`
    : "";
  const label = `${workflowLabel}${versionLabel} routed graph`;
  const description = buildGraphDescription(
    nodes.length,
    edges.length,
    appliedTransitionCount,
    appliedDecisionCount,
    invalidElementCount,
    sourceContext,
  );
  const contentKey = JSON.stringify({
    label,
    description,
    nodes,
    edges,
    authoredNodeCount: authoredNodes.length,
    placeholderNodeCount: sortedPlaceholders.length,
    appliedTransitionCount,
    appliedDecisionCount,
    responsibilityLanes,
    workflowDecisionGates,
    workflowDecisionGateCount,
    malformedEntryCount,
    invalidElementCount,
  });

  return {
    contentKey,
    label,
    description,
    nodes,
    edges,
    authoredNodeCount: authoredNodes.length,
    placeholderNodeCount: sortedPlaceholders.length,
    appliedTransitionCount,
    appliedDecisionCount,
    responsibilityLanes,
    workflowDecisionGates,
    workflowDecisionGateCount,
    malformedEntryCount,
    invalidElementCount,
  };
}

function toNodeRecord(item: IndexedValue): ProjectedNodeRecord {
  const value = asRecord(item.value);
  const transitions = countTransitions(value?.["allowedTransitions"]);
  const directDecisions = countDecisions(value?.["requiredDecisions"]);
  const decisionOrigins = collectDecisionOrigins(
    value?.["requiredDecisions"],
    value?.["allowedTransitions"],
  );
  const displayOrderValue = value?.["displayOrder"];
  const displayOrder = typeof displayOrderValue === "number" && Number.isFinite(displayOrderValue)
    ? displayOrderValue
    : null;

  return {
    index: item.index,
    sourceId: readString(value?.["id"]),
    typeCode: readString(value?.["typeCode"]),
    displayOrder,
    stepCode: readNullableString(value?.["stepCode"]),
    displayName: readNullableString(value?.["displayName"]),
    conditionCode: readNullableString(asRecord(value?.["routing"])?.["conditionCode"]),
    conditionVersion: typeof asRecord(value?.["routing"])?.["conditionVersion"] === "number" ? asRecord(value?.["routing"])!["conditionVersion"] as number : null,
    transitions,
    directDecisions,
    decisionOrigins,
    hasMalformedShape: !value
      || (displayOrderValue !== undefined && displayOrder === null)
      || collectionShapeErrorCount(value["allowedTransitions"]) > 0
      || collectionShapeErrorCount(value["requiredDecisions"]) > 0,
  };
}

function toEdgeRecord(item: IndexedValue): ProjectedEdgeRecord {
  const value = asRecord(item.value);

  return {
    index: item.index,
    sourceId: readString(value?.["id"]),
    fromSourceNodeId: readString(value?.["fromNodeId"]),
    toSourceNodeId: readString(value?.["toNodeId"]),
    outcomeCode: readNullableString(value?.["outcomeCode"]),
    pathKindCode: readNullableString(value?.["pathKindCode"]),
    hasMalformedShape: !value,
  };
}

function toGraphNode(
  node: ProjectedNodeRecord,
  occurrence: number,
  hasDuplicateSourceId: boolean,
  processOrder: number | null,
  sourceContext: WorkflowGraphProjectionSourceContext,
): WorkflowReadOnlyGraphNode {
  const kind = toNodeKind(node.typeCode);
  const groupLabel = toNodeGroupLabel(kind);
  const sourceIdLabel = displayValue(node.sourceId);
  const stepCodeLabel = displayValue(node.stepCode);
  const displayOrderLabel = node.displayOrder === null ? "Not provided" : String(node.displayOrder);
  const appliedDecisionCount = node.directDecisions.applied + node.transitions.decisions.applied;
  const malformedDecisionCount = node.directDecisions.malformed + node.transitions.decisions.malformed;
  const label = buildNodeLabel(node, kind);
  const decisionGates = kind === "human-step"
    ? toDecisionGates(node.decisionOrigins, label, node.sourceId, node.index)
    : [];
  const isInvalid = node.hasMalformedShape
    || kind === "invalid"
    || !hasVisibleValue(node.sourceId)
    || hasDuplicateSourceId
    || (["human-step", "automated-step"].includes(kind) && !hasVisibleValue(node.stepCode));
  const facts: WorkflowReadOnlyGraphFact[] = [
    { label: "Stable node ID", value: sourceIdLabel },
    { label: "Node type", value: displayValue(node.typeCode) },
    { label: "Display order", value: displayOrderLabel },
    { label: "Step code", value: stepCodeLabel },
    ...(node.conditionCode ? [{ label: "Routing condition", value: `${node.conditionCode} v${node.conditionVersion}` }] : []),
    {
      label: sourceContext === "published-version"
        ? "Saved transition entries"
        : "Draft transition entries",
      value: String(node.transitions.applied),
    },
    {
      label: sourceContext === "published-version"
        ? "Saved decision entries"
        : "Draft decision entries",
      value: String(appliedDecisionCount),
    },
    { label: "Effective decision gates", value: String(decisionGates.filter((gate) => !gate.isInvalid).length) },
  ];

  if (processOrder !== null) {
    facts.push({ label: "Process order", value: String(processOrder) });
  }

  if (hasDuplicateSourceId) {
    facts.push({ label: "Stable ID status", value: "Duplicate stable node ID" });
  }
  if (node.transitions.malformed > 0 || malformedDecisionCount > 0) {
    facts.push({
      label: "Malformed entries",
      value: `${node.transitions.malformed} transition, ${malformedDecisionCount} decision`,
    });
  }

  const description = [
    `${groupLabel} ${label}.`,
    `Stable node ID ${sourceIdLabel}.`,
    `${node.transitions.applied} ${sourceContext === "published-version" ? "saved" : "draft"} transition ${entryWord(node.transitions.applied)} and ${appliedDecisionCount} ${sourceContext === "published-version" ? "saved" : "draft"} decision ${entryWord(appliedDecisionCount)}.`,
    isInvalid
      ? `This node contains invalid or incomplete ${sourceContext === "published-version" ? "immutable version document" : "draft"} data.`
      : "",
  ].filter(Boolean).join(" ");
  const detail: WorkflowReadOnlyGraphElementDetail = {
    title: label,
    subtitle: groupLabel,
    description,
    facts,
  };

  return {
    id: visualizationId("node", node.sourceId, occurrence),
    sourceId: node.sourceId,
    sourceIndex: node.index,
    kind,
    responsibilityLaneId: WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_STAGES_LANE_ID,
    groupLabel,
    label,
    description,
    width: WORKFLOW_READ_ONLY_GRAPH_NODE_DIMENSIONS.width,
    height: WORKFLOW_READ_ONLY_GRAPH_NODE_DIMENSIONS.height,
    displayOrder: node.displayOrder,
    processOrder,
    stepCode: node.stepCode,
    decisionGates,
    appliedTransitionCount: node.transitions.applied,
    appliedDecisionCount,
    malformedTransitionCount: node.transitions.malformed,
    malformedDecisionCount,
    isInvalid,
    detail,
  };
}

function toGraphEdge(
  edge: ProjectedEdgeRecord,
  occurrence: number,
  from: EndpointResolution,
  to: EndpointResolution,
  hasDuplicateSourceId: boolean,
): WorkflowReadOnlyGraphEdge {
  const isInvalid = edge.hasMalformedShape
    || !hasVisibleValue(edge.sourceId)
    || hasDuplicateSourceId
    || from.isInvalid
    || to.isInvalid;
  const groupLabel = isInvalid ? "Invalid route" : "Route";
  const label = `${edge.outcomeCode ? formatCodeLabel(edge.outcomeCode) + ": " : ""}${from.displayValue} to ${to.displayValue}${edge.pathKindCode === "return" ? " (return)" : ""}`;
  const description = [
    `${groupLabel} from ${from.displayValue} to ${to.displayValue}.`,
    `Stable edge ID ${displayValue(edge.sourceId)}.`,
    isInvalid ? "One or more route references are invalid or ambiguous." : "",
  ].filter(Boolean).join(" ");
  const facts: WorkflowReadOnlyGraphFact[] = [
    { label: "Stable edge ID", value: displayValue(edge.sourceId) },
    { label: "From node ID", value: displayValue(edge.fromSourceNodeId) },
    { label: "From endpoint status", value: from.status },
    { label: "To node ID", value: displayValue(edge.toSourceNodeId) },
    { label: "To endpoint status", value: to.status },
    ...(edge.outcomeCode ? [{ label: "Routing result", value: edge.outcomeCode }] : []),
    ...(edge.pathKindCode ? [{ label: "Path behavior", value: edge.pathKindCode }] : []),
  ];
  if (hasDuplicateSourceId) {
    facts.push({ label: "Stable ID status", value: "Duplicate stable edge ID" });
  }

  return {
    id: visualizationId("edge", edge.sourceId, occurrence),
    sourceId: edge.sourceId,
    sourceIndex: edge.index,
    ...(edge.pathKindCode ? {pathKindCode:edge.pathKindCode} : {}),
    ...(edge.outcomeCode ? {outcomeCode:edge.outcomeCode} : {}),
    source: from.visualizationId,
    target: to.visualizationId,
    fromSourceNodeId: edge.fromSourceNodeId,
    toSourceNodeId: edge.toSourceNodeId,
    fromDisplayValue: from.displayValue,
    toDisplayValue: to.displayValue,
    kind: isInvalid ? "invalid" : "route",
    groupLabel,
    label,
    ...(edge.outcomeCode ? { resultLabel: `${formatCodeLabel(edge.outcomeCode)}${edge.pathKindCode === "return" ? " (return)" : ""}` } : {}),
    description,
    labelWidth: WORKFLOW_READ_ONLY_GRAPH_EDGE_LABEL_DIMENSIONS.width,
    labelHeight: WORKFLOW_READ_ONLY_GRAPH_EDGE_LABEL_DIMENSIONS.height,
    isInvalid,
    detail: {
      title: label,
      subtitle: groupLabel,
      description,
      facts,
    },
  };
}

function resolveEndpoint(
  sourceNodeId: string,
  endpointRole: "from" | "to",
  nodesBySourceId: ReadonlyMap<string, readonly WorkflowReadOnlyGraphNode[]>,
  placeholders: Map<string, WorkflowReadOnlyGraphNode>,
  sourceContext: WorkflowGraphProjectionSourceContext,
): EndpointResolution {
  const matches = hasVisibleValue(sourceNodeId)
    ? nodesBySourceId.get(sourceNodeId) ?? []
    : [];
  if (matches.length === 1) {
    return {
      visualizationId: matches[0].id,
      displayValue: matches[0].label,
      isInvalid: matches[0].isInvalid,
      status: matches[0].isInvalid ? "Matched an invalid node" : "Matched",
    };
  }

  if (matches.length > 1) {
    return {
      visualizationId: matches[0].id,
      displayValue: `${matches[0].label} (duplicate ID)`,
      isInvalid: true,
      status: `Ambiguous; matched ${matches.length} nodes`,
    };
  }

  const placeholderKey = hasVisibleValue(sourceNodeId)
    ? `missing:${sourceNodeId}`
    : `missing:${endpointRole}:blank`;
  let placeholder = placeholders.get(placeholderKey);
  if (!placeholder) {
    placeholder = toMissingEndpointNode(sourceNodeId, endpointRole, sourceContext);
    placeholders.set(placeholderKey, placeholder);
  }

  return {
    visualizationId: placeholder.id,
    displayValue: placeholder.label,
    isInvalid: true,
    status: "Missing node",
  };
}

function toMissingEndpointNode(
  sourceNodeId: string,
  endpointRole: "from" | "to",
  sourceContext: WorkflowGraphProjectionSourceContext,
): WorkflowReadOnlyGraphNode {
  const sourceIdLabel = displayValue(sourceNodeId);
  const label = hasVisibleValue(sourceNodeId)
    ? `${formatCodeLabel(sourceNodeId)} (missing)`
    : `Missing ${endpointRole} node ID`;
  const description = `Missing route endpoint ${sourceIdLabel}. No authored node matches this edge reference.`;
  const facts: WorkflowReadOnlyGraphFact[] = [
    { label: "Referenced node ID", value: sourceIdLabel },
    {
      label: "Endpoint status",
      value: sourceContext === "published-version"
        ? "Missing from the immutable version node collection"
        : "Missing from the draft node collection",
    },
  ];

  return {
    id: visualizationId(`missing-${endpointRole}`, sourceNodeId, 1),
    sourceId: sourceNodeId,
    sourceIndex: null,
    kind: "missing-endpoint",
    responsibilityLaneId: WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_STAGES_LANE_ID,
    groupLabel: "Missing endpoint",
    label,
    description,
    width: WORKFLOW_READ_ONLY_GRAPH_PLACEHOLDER_NODE_DIMENSIONS.width,
    height: WORKFLOW_READ_ONLY_GRAPH_PLACEHOLDER_NODE_DIMENSIONS.height,
    displayOrder: null,
    processOrder: null,
    stepCode: null,
    decisionGates: [],
    appliedTransitionCount: 0,
    appliedDecisionCount: 0,
    malformedTransitionCount: 0,
    malformedDecisionCount: 0,
    isInvalid: true,
    detail: {
      title: label,
      subtitle: "Missing endpoint",
      description,
      facts,
    },
  };
}

function collectDecisionOrigins(
  directDecisionValue: unknown,
  transitionValue: unknown,
): ProjectedDecisionOrigin[] {
  const origins: ProjectedDecisionOrigin[] = [];
  const directApplication: DecisionApplication = {
    kind: "all",
    transitionIndex: null,
    fromStateCode: null,
    toStateCode: null,
    isMalformed: false,
  };
  appendDecisionCollectionOrigins(
    origins,
    directDecisionValue,
    "direct:structured",
    directApplication,
    false,
  );

  arrayValue(transitionValue).forEach((entry, transitionIndex) => {
    const transition = asRecord(entry);
    if (!transition) {
      return;
    }

    const fromStateCode = readOptionalCode(transition["fromStateCode"]);
    const toStateCode = readOptionalCode(transition["toStateCode"]);
    const application: DecisionApplication = {
      kind: "transition",
      transitionIndex,
      fromStateCode: fromStateCode.value,
      toStateCode: toStateCode.value,
      isMalformed: fromStateCode.isMalformed || toStateCode.isMalformed,
    };
    appendDecisionCollectionOrigins(
      origins,
      transition["requiredDecisions"],
      `transition:${transitionIndex}:structured`,
      application,
      false,
    );
    appendDecisionCollectionOrigins(
      origins,
      transition["requiredDecisionKinds"],
      `transition:${transitionIndex}:legacy`,
      application,
      true,
    );
  });

  return origins;
}

function appendDecisionCollectionOrigins(
  origins: ProjectedDecisionOrigin[],
  value: unknown,
  originPrefix: string,
  application: DecisionApplication,
  isLegacy: boolean,
): void {
  if (value === null || value === undefined) {
    return;
  }
  if (!Array.isArray(value)) {
    origins.push(invalidDecisionOrigin(
      `${originPrefix}:collection`,
      origins.length,
      null,
      application,
      isLegacy ? "legacy" : "structured",
    ));
    return;
  }

  value.forEach((entry, index) => {
    origins.push(isLegacy
      ? toLegacyDecisionOrigin(
        entry,
        `${originPrefix}:${index}`,
        origins.length,
        index,
        application,
      )
      : toStructuredDecisionOrigin(
        entry,
        `${originPrefix}:${index}`,
        origins.length,
        index,
        application,
      ));
  });
}

function toLegacyDecisionOrigin(
  value: unknown,
  id: string,
  ordinal: number,
  decisionIndex: number,
  application: DecisionApplication,
): ProjectedDecisionOrigin {
  const decisionKindCode = readOptionalCode(value);
  return {
    id,
    ordinal,
    decisionIndex,
    decisionKindCode: decisionKindCode.value,
    requiredOutcomeCode: "approved",
    reactionCode: "permit",
    targetStateCode: null,
    sourceKind: "legacy",
    ownerQueueCode: null,
    eligibleRoleCodes: [],
    notificationCount: 0,
    application,
    isMalformed: application.isMalformed || decisionKindCode.isMalformed,
  };
}

function toStructuredDecisionOrigin(
  value: unknown,
  id: string,
  ordinal: number,
  decisionIndex: number,
  application: DecisionApplication,
): ProjectedDecisionOrigin {
  const decision = asRecord(value);
  if (!decision) {
    return invalidDecisionOrigin(id, ordinal, decisionIndex, application, "structured");
  }

  const decisionKindCode = readOptionalCode(decision["decisionKind"]);
  const requiredOutcomeCode = readDefaultedCode(
    decision["requiredOutcomeCode"],
    "approved",
  );
  const reaction = readDecisionReaction(decision["reaction"]);
  const eligibleRoleCodes = readStringCodeCollection(decision["eligibleRoleCodes"]);
  const ownerQueueCode = readNullableCode(decision["ownerQueueCode"]);
  const notificationCount = Array.isArray(decision["notifications"])
    ? decision["notifications"].length
    : 0;
  const hasMalformedGuidance = eligibleRoleCodes.isMalformed
    || ownerQueueCode.isMalformed
    || hasMalformedNotificationCollection(decision["notifications"]);
  const normalizedReactionCode = normalizeSemanticCode(reaction.reactionCode);
  const hasInertTarget = reaction.targetStateCode !== null
    && !TARGET_BEARING_REACTION_CODES.has(normalizedReactionCode);
  const hasUnsupportedReaction = reaction.reactionCode !== null
    && !SUPPORTED_REACTION_CODES.has(normalizedReactionCode);

  return {
    id,
    ordinal,
    decisionIndex,
    decisionKindCode: decisionKindCode.value,
    requiredOutcomeCode: requiredOutcomeCode.value,
    reactionCode: reaction.reactionCode,
    targetStateCode: reaction.targetStateCode,
    sourceKind: "structured",
    ownerQueueCode: ownerQueueCode.value,
    eligibleRoleCodes: eligibleRoleCodes.values,
    notificationCount,
    application,
    isMalformed: application.isMalformed
      || decisionKindCode.isMalformed
      || requiredOutcomeCode.isMalformed
      || reaction.isMalformed
      || hasMalformedGuidance
      || hasInertTarget
      || hasUnsupportedReaction,
  };
}

function invalidDecisionOrigin(
  id: string,
  ordinal: number,
  decisionIndex: number | null,
  application: DecisionApplication,
  sourceKind: "structured" | "legacy" = "structured",
): ProjectedDecisionOrigin {
  return {
    id,
    ordinal,
    decisionIndex,
    decisionKindCode: null,
    requiredOutcomeCode: null,
    reactionCode: null,
    targetStateCode: null,
    sourceKind,
    ownerQueueCode: null,
    eligibleRoleCodes: [],
    notificationCount: 0,
    application,
    isMalformed: true,
  };
}

function groupDecisionOrigins(
  origins: readonly ProjectedDecisionOrigin[],
): ProjectedDecisionGroup[] {
  const groups = new Map<string, ProjectedDecisionOrigin[]>();
  origins.forEach((origin) => {
    const semanticKey = decisionSemanticKey(origin);
    const key = semanticKey ?? `invalid:${origin.id}`;
    const matches = groups.get(key) ?? [];
    matches.push(origin);
    groups.set(key, matches);
  });

  return [...groups.entries()]
    .map(([key, matches]): ProjectedDecisionGroup => {
      const first = matches[0];
      return {
        key,
        decisionKindCode: first.decisionKindCode,
        requiredOutcomeCode: first.requiredOutcomeCode,
        reactionCode: first.reactionCode,
        targetStateCode: first.targetStateCode,
        origins: matches,
        isInvalid: matches.some((origin) => origin.isMalformed)
          || decisionSemanticKey(first) === null,
      };
    })
    .sort((left, right) => compareNumber(
      left.origins[0].ordinal,
      right.origins[0].ordinal,
    ));
}

function toDecisionGates(
  origins: readonly ProjectedDecisionOrigin[],
  ownerLabel: string,
  ownerSourceId: string,
  ownerSourceIndex: number | null,
): WorkflowReadOnlyGraphDecisionGate[] {
  return groupDecisionOrigins(origins).map((group) => {
    const directApplication = group.origins.some(
      (origin) => origin.application.kind === "all",
    );
    const affectedTransitionIndexes = new Set(group.origins.flatMap((origin) =>
      origin.application.transitionIndex === null
        ? []
        : [origin.application.transitionIndex]));
    const malformedOriginCount = group.origins.filter((origin) => origin.isMalformed).length;
    const title = gateTitle(group.decisionKindCode);
    const effect = gateEffect(group, directApplication);
    const decisionKindLabel = formatCodeLabel(group.decisionKindCode);
    const configuredOutcomeLabel = formatCodeLabel(group.requiredOutcomeCode);
    const matchedOutcomeLabel = configuredOutcomeLabel || "Outcome needs attention";
    const matchedEffect = gateMatchedEffect(group, directApplication);
    const blockedFallback = group.isInvalid
      ? "Fallback unknown until this saved requirement is repaired"
      : "Missing or different result · status change blocked";
    const scope = gateScope(group, ownerLabel, directApplication);
    const guidance = projectDecisionGuidance(group, ownerSourceIndex === null);
    const authoredOrigins = group.origins.map(toDecisionGateOrigin);
    const notificationCount = group.origins.reduce(
      (total, origin) => total + origin.notificationCount,
      0,
    );
    const isLegacyOnly = group.origins.every((origin) => origin.sourceKind === "legacy");
    const umlQuestion = decisionKindLabel && configuredOutcomeLabel
      ? `${decisionKindLabel} result?`
      : "Decision requirement complete?";
    const applicationDescription = gateApplicationDescription(
      group,
      ownerLabel,
      directApplication,
    );
    const originDescription = group.origins.length === 1
      ? "It comes from one saved requirement."
      : `It combines ${group.origins.length} semantically identical saved requirements.`;
    const invalidDescription = group.isInvalid
      ? " Saved evidence is incomplete or malformed and requires correction."
      : "";
    const unmetResultDescription = group.isInvalid
      ? ""
      : " If the required decision is missing or has a different result, the requested status change remains blocked.";

    return {
      id: decisionGateId(ownerSourceId, ownerSourceIndex, group.key),
      ownerLabel,
      ownerSourceId,
      ownerSourceIndex,
      title,
      umlQuestion,
      matchedOutcomeLabel,
      matchedEffect,
      blockedFallback,
      scopeKind: scope.kind,
      scopeLabel: scope.label,
      responsibilityLaneId: guidance.laneId,
      responsibilityLaneKind: guidance.laneKind,
      queueGuidanceCodes: guidance.queueCodes,
      suggestedRoleCodes: guidance.roleCodes,
      notificationCount,
      isLegacyOnly,
      authoredOrigins,
      effect,
      accessibleDescription:
        `${ownerLabel}: ${umlQuestion} ${matchedOutcomeLabel}: ${matchedEffect}. ${blockedFallback}. ${scope.label}. ${applicationDescription} ${originDescription}`
        + unmetResultDescription
        + guidanceDescription(guidance)
        + (notificationCount === 0
          ? " No notifications are saved on this requirement."
          : ` ${notificationCount} saved notification rule${pluralSuffix(notificationCount)}.`)
        + " This decision gate controls status changes; it does not create or branch a workflow route."
        + " This summary reflects saved configuration; authoritative validation determines whether it can execute."
        + invalidDescription,
      decisionKindCode: group.decisionKindCode,
      requiredOutcomeCode: group.requiredOutcomeCode,
      reactionCode: group.reactionCode,
      targetStateCode: group.targetStateCode,
      appliesToEveryStatusChange: directApplication,
      affectedTransitionCount: affectedTransitionIndexes.size,
      authoredOriginCount: group.origins.length,
      malformedOriginCount,
      isInvalid: group.isInvalid,
    };
  });
}

function toDecisionGateOrigin(
  origin: ProjectedDecisionOrigin,
): WorkflowReadOnlyGraphDecisionGateOrigin {
  return {
    id: origin.id,
    sourceKind: origin.sourceKind,
    locationKind: origin.application.kind === "all" ? "direct" : "transition",
    decisionIndex: origin.decisionIndex,
    scopeKind: origin.application.kind,
    transitionIndex: origin.application.transitionIndex,
    fromStateCode: origin.application.fromStateCode,
    toStateCode: origin.application.toStateCode,
    queueGuidanceCode: origin.ownerQueueCode,
    suggestedRoleCodes: origin.eligibleRoleCodes,
    notificationCount: origin.notificationCount,
    isMalformed: origin.isMalformed,
  };
}

function projectDecisionGuidance(
  group: ProjectedDecisionGroup,
  isWorkflowWide: boolean,
): ProjectedDecisionGuidance {
  const queueCodes = sortedCodes(uniqueVisibleCodes(
    group.origins.map((origin) => origin.ownerQueueCode),
  ));
  const roleCodes = sortedCodes(uniqueVisibleCodes(
    group.origins.flatMap((origin) => origin.eligibleRoleCodes),
  ));

  if (isWorkflowWide) {
    return {
      laneId: WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_REQUIREMENTS_LANE_ID,
      laneKind: "workflow-requirements",
      queueCodes,
      roleCodes,
    };
  }
  if (group.isInvalid) {
    return {
      laneId: responsibilityLaneId("needs-attention", []),
      laneKind: "needs-attention",
      queueCodes,
      roleCodes,
    };
  }

  const guidanceSignatures = new Set(group.origins.map(decisionGuidanceSignature));
  if (guidanceSignatures.size > 1 || queueCodes.length > 1) {
    return {
      laneId: responsibilityLaneId("mixed-guidance", []),
      laneKind: "mixed-guidance",
      queueCodes,
      roleCodes,
    };
  }
  if (queueCodes.length === 1) {
    return {
      laneId: responsibilityLaneId("queue-guidance", queueCodes),
      laneKind: "queue-guidance",
      queueCodes,
      roleCodes,
    };
  }
  if (roleCodes.length > 0) {
    return {
      laneId: responsibilityLaneId("role-guidance", roleCodes),
      laneKind: "role-guidance",
      queueCodes,
      roleCodes,
    };
  }
  return {
    laneId: responsibilityLaneId("unassigned-guidance", []),
    laneKind: "unassigned-guidance",
    queueCodes,
    roleCodes,
  };
}

function decisionGuidanceSignature(origin: ProjectedDecisionOrigin): string {
  const queue = normalizeSemanticCode(origin.ownerQueueCode);
  const roles = origin.eligibleRoleCodes
    .map(normalizeSemanticCode)
    .filter(Boolean)
    .sort(compareText)
    .join(",");
  return `${queue}|${roles}`;
}

function responsibilityLaneId(
  kind: WorkflowReadOnlyGraphResponsibilityLaneKind,
  guidanceCodes: readonly string[],
): string {
  const suffix = guidanceCodes.length === 0
    ? kind
    : guidanceCodes.map(normalizeSemanticCode).sort(compareText).join("|");
  return `responsibility-lane:${kind}:${encodeURIComponent(suffix)}`;
}

function guidanceDescription(guidance: ProjectedDecisionGuidance): string {
  if (guidance.laneKind === "workflow-requirements") {
    return guidanceMetadataDescription(guidance);
  }
  if (guidance.laneKind === "needs-attention") {
    return " Responsibility guidance is incomplete or malformed and is shown in Needs attention.";
  }
  if (guidance.laneKind === "mixed-guidance") {
    return ` Saved requirements contain mixed responsibility guidance.${guidanceMetadataDescription(guidance)}`;
  }
  if (guidance.laneKind === "queue-guidance") {
    return ` Intended decision queue ${formatCodeLabel(guidance.queueCodes[0])}.${roleGuidanceDescription(guidance.roleCodes)}`;
  }
  if (guidance.laneKind === "role-guidance") {
    return ` No intended decision queue is saved.${roleGuidanceDescription(guidance.roleCodes)}`;
  }
  return " No intended decision queue or suggested role is saved.";
}

function guidanceMetadataDescription(guidance: ProjectedDecisionGuidance): string {
  const queueDescription = guidance.queueCodes.length === 0
    ? " No intended decision queue is saved."
    : ` Intended decision queue guidance: ${guidance.queueCodes.map(formatCodeLabel).join(", ")}.`;
  return `${queueDescription}${roleGuidanceDescription(guidance.roleCodes)}`;
}

function roleGuidanceDescription(roleCodes: readonly string[]): string {
  return roleCodes.length === 0
    ? " No suggested roles are saved."
    : ` Suggested roles: ${roleCodes.map(formatCodeLabel).join(", ")}.`;
}

function gateScope(
  group: ProjectedDecisionGroup,
  ownerLabel: string,
  appliesToEveryStatusChange: boolean,
): ProjectedDecisionScope {
  if (appliesToEveryStatusChange) {
    return {
      kind: "every-status-change",
      label: `Every status change in ${ownerLabel}`,
    };
  }

  const transitionDescriptions = uniqueTexts(group.origins.flatMap((origin) => {
    const application = origin.application;
    return application.kind === "transition"
      && hasVisibleValue(application.fromStateCode)
      && hasVisibleValue(application.toStateCode)
      ? [`${formatCodeLabel(application.fromStateCode)} → ${formatCodeLabel(application.toStateCode)}`]
      : [];
  }));
  if (transitionDescriptions.length === 1) {
    return {
      kind: "specific-status-change",
      label: `${transitionDescriptions[0]} in ${ownerLabel}`,
    };
  }
  if (transitionDescriptions.length > 1) {
    return {
      kind: "multiple-status-changes",
      label: `${transitionDescriptions.length} status changes in ${ownerLabel}`,
    };
  }
  return {
    kind: "incomplete",
    label: `Incomplete saved status-change scope in ${ownerLabel}`,
  };
}

function gateMatchedEffect(
  group: ProjectedDecisionGroup,
  appliesToEveryStatusChange: boolean,
): string {
  if (
    group.isInvalid
    || !hasVisibleValue(group.requiredOutcomeCode)
    || !hasVisibleValue(group.reactionCode)
  ) {
    return "Saved effect needs attention";
  }

  const reactionCode = normalizeSemanticCode(group.reactionCode);
  if (
    hasVisibleValue(group.targetStateCode)
    && TARGET_BEARING_REACTION_CODES.has(reactionCode)
  ) {
    return `Redirect requested status change to ${formatCodeLabel(group.targetStateCode)}`;
  }
  if (reactionCode === "block") {
    return appliesToEveryStatusChange
      ? "Block every status change"
      : "Block requested status change";
  }
  if (appliesToEveryStatusChange) {
    return "Allow every status change";
  }

  const targetStates = uniqueVisibleCodes(group.origins.map(
    (origin) => origin.application.toStateCode,
  ));
  if (targetStates.length === 1) {
    const effect = allowedStatusChangeEffect(targetStates[0]);
    return effect.startsWith("allows ")
      ? `Allow ${effect.slice("allows ".length)}`
      : sentenceCase(effect);
  }
  if (targetStates.length > 1) {
    return `Allow ${targetStates.length} configured status changes`;
  }
  return "Allow requested status change";
}

function toResponsibilityLanes(
  nodes: readonly WorkflowReadOnlyGraphNode[],
): WorkflowReadOnlyGraphResponsibilityLane[] {
  const gatesByLaneId = new Map<string, WorkflowReadOnlyGraphDecisionGate[]>();
  nodes.forEach((node) => {
    (node.decisionGates ?? []).forEach((gate) => {
      const gates = gatesByLaneId.get(gate.responsibilityLaneId) ?? [];
      gates.push(gate);
      gatesByLaneId.set(gate.responsibilityLaneId, gates);
    });
  });

  const guidanceLanes = [...gatesByLaneId.entries()]
    .map(([id, gates]): WorkflowReadOnlyGraphResponsibilityLane => {
      const kind = gates[0].responsibilityLaneKind;
      const queueGuidanceCodes = sortedCodes(uniqueVisibleCodes(
        gates.flatMap((gate) => gate.queueGuidanceCodes),
      ));
      const suggestedRoleCodes = sortedCodes(uniqueVisibleCodes(
        gates.flatMap((gate) => gate.suggestedRoleCodes),
      ));
      return {
        id,
        kind,
        scope: "step-decisions",
        label: responsibilityLaneLabel(kind, queueGuidanceCodes, suggestedRoleCodes),
        description: responsibilityLaneDescription(kind),
        queueGuidanceCodes,
        suggestedRoleCodes,
        gateCount: gates.length,
      };
    })
    .sort(compareResponsibilityLanes);

  return [
    {
      id: WORKFLOW_READ_ONLY_GRAPH_WORKFLOW_STAGES_LANE_ID,
      kind: "stage",
      scope: "workflow-stages",
      label: "Workflow stages",
      description:
        "Authored stages and routes. This central lane does not claim live queue ownership or assignment.",
      queueGuidanceCodes: [],
      suggestedRoleCodes: [],
      gateCount: 0,
    },
    ...guidanceLanes,
  ];
}

function responsibilityLaneLabel(
  kind: WorkflowReadOnlyGraphResponsibilityLaneKind,
  queueCodes: readonly string[],
  roleCodes: readonly string[],
): string {
  switch (kind) {
    case "queue-guidance":
      return `Decision guidance · ${formatCodeLabel(queueCodes[0])}`;
    case "role-guidance":
      return `Role guidance · ${roleCodes.map(formatCodeLabel).join(" or ")}`;
    case "mixed-guidance":
      return "Mixed decision guidance";
    case "needs-attention":
      return "Decision guidance needs attention";
    case "unassigned-guidance":
      return "Unassigned decision guidance";
    case "workflow-requirements":
      return "Whole workflow requirements";
    default:
      return "Workflow stages";
  }
}

function responsibilityLaneDescription(
  kind: WorkflowReadOnlyGraphResponsibilityLaneKind,
): string {
  switch (kind) {
    case "queue-guidance":
      return "Saved intended queue for a decision. It does not assign the stage or live work.";
    case "role-guidance":
      return "Saved suggested roles for a decision. It does not grant access or create a quorum.";
    case "mixed-guidance":
      return "Semantically identical saved requirements contain different queue or role guidance.";
    case "needs-attention":
      return "Saved responsibility evidence is incomplete or malformed and must not be inferred.";
    case "unassigned-guidance":
      return "No intended decision queue or suggested role is saved.";
    case "workflow-requirements":
      return "Whole-workflow requirements remain separate from step responsibility guidance.";
    default:
      return "Authored workflow stages and routes.";
  }
}

function compareResponsibilityLanes(
  left: WorkflowReadOnlyGraphResponsibilityLane,
  right: WorkflowReadOnlyGraphResponsibilityLane,
): number {
  const kindOrder: Readonly<Record<WorkflowReadOnlyGraphResponsibilityLaneKind, number>> = {
    stage: 0,
    "queue-guidance": 1,
    "role-guidance": 2,
    "mixed-guidance": 3,
    "unassigned-guidance": 4,
    "needs-attention": 5,
    "workflow-requirements": 6,
  };
  return compareNumber(kindOrder[left.kind], kindOrder[right.kind])
    || compareText(left.label, right.label)
    || compareText(left.id, right.id);
}

function decisionSemanticKey(origin: ProjectedDecisionOrigin): string | null {
  if (
    !hasVisibleValue(origin.decisionKindCode)
    || !hasVisibleValue(origin.requiredOutcomeCode)
    || !hasVisibleValue(origin.reactionCode)
  ) {
    return null;
  }

  return [
    origin.decisionKindCode,
    origin.requiredOutcomeCode,
    origin.reactionCode,
    origin.targetStateCode ?? "",
  ].map(normalizeSemanticCode).join("|");
}

function gateTitle(decisionKindCode: string | null): string {
  const label = formatCodeLabel(decisionKindCode);
  return label ? `${label} required` : "Incomplete decision requirement";
}

function gateEffect(
  group: ProjectedDecisionGroup,
  appliesToEveryStatusChange: boolean,
): string {
  if (group.isInvalid) {
    return "Needs attention · incomplete or invalid saved requirement";
  }
  if (
    !hasVisibleValue(group.requiredOutcomeCode)
    || !hasVisibleValue(group.reactionCode)
  ) {
    return "Needs attention · incomplete saved requirement";
  }

  const outcomeLabel = formatCodeLabel(group.requiredOutcomeCode);
  const reactionCode = normalizeSemanticCode(group.reactionCode);
  if (
    hasVisibleValue(group.targetStateCode)
    && TARGET_BEARING_REACTION_CODES.has(reactionCode)
  ) {
    return `${outcomeLabel} · redirects the requested change to ${formatCodeLabel(group.targetStateCode)}; otherwise blocked`;
  }
  if (reactionCode === "block") {
    return appliesToEveryStatusChange
      ? `${outcomeLabel} · stops every status change; otherwise blocked`
      : `${outcomeLabel} · stops the status change; otherwise blocked`;
  }
  if (appliesToEveryStatusChange) {
    return `${outcomeLabel} · allows every status change; otherwise blocked`;
  }

  const targetStates = uniqueVisibleCodes(group.origins.map(
    (origin) => origin.application.toStateCode,
  ));
  if (targetStates.length === 1) {
    return `${outcomeLabel} · ${allowedStatusChangeEffect(targetStates[0])}; otherwise blocked`;
  }
  if (targetStates.length > 1) {
    return `${outcomeLabel} · allows ${targetStates.length} status changes; otherwise blocked`;
  }
  return `${outcomeLabel} · allows a status change; otherwise blocked`;
}

function gateApplicationDescription(
  group: ProjectedDecisionGroup,
  ownerLabel: string,
  appliesToEveryStatusChange: boolean,
): string {
  if (appliesToEveryStatusChange) {
    return `It applies to every status change at ${ownerLabel}.`;
  }

  const transitionDescriptions = uniqueTexts(group.origins.flatMap((origin) => {
    const application = origin.application;
    if (
      application.kind !== "transition"
      || !hasVisibleValue(application.fromStateCode)
      || !hasVisibleValue(application.toStateCode)
    ) {
      return [];
    }
    return [
      `${formatCodeLabel(application.fromStateCode)} to ${formatCodeLabel(application.toStateCode)}`,
    ];
  }));
  if (transitionDescriptions.length === 1) {
    return `It applies to ${transitionDescriptions[0]} at ${ownerLabel}.`;
  }
  if (transitionDescriptions.length > 1) {
    return `It applies to ${transitionDescriptions.length} status changes at ${ownerLabel}.`;
  }
  return `Its saved status-change location at ${ownerLabel} is incomplete.`;
}

function allowedStatusChangeEffect(targetStateCode: string): string {
  switch (normalizeSemanticCode(targetStateCode)) {
    case "complete":
    case "completed":
      return "allows completion";
    case "return":
    case "returned":
      return "allows return";
    case "lock":
    case "locked":
      return "allows locking";
    case "cancel":
    case "canceled":
    case "cancelled":
      return "allows cancellation";
    default:
      return `allows the change to ${formatCodeLabel(targetStateCode)}`;
  }
}

function decisionGateId(
  ownerSourceId: string,
  ownerSourceIndex: number | null,
  semanticKey: string,
): string {
  const owner = encodeURIComponent(hasVisibleValue(ownerSourceId) ? ownerSourceId : "not-provided");
  const ownerLocation = ownerSourceIndex === null ? "workflow" : `node-${ownerSourceIndex}`;
  return `decision-gate:${ownerLocation}:${owner}:${encodeURIComponent(semanticKey)}`;
}

function readDecisionReaction(value: unknown): {
  readonly reactionCode: string | null;
  readonly targetStateCode: string | null;
  readonly isMalformed: boolean;
} {
  if (value === null || value === undefined) {
    return { reactionCode: "permit", targetStateCode: null, isMalformed: false };
  }

  const reaction = asRecord(value);
  if (!reaction) {
    return { reactionCode: null, targetStateCode: null, isMalformed: true };
  }

  const reactionCode = readOptionalCode(reaction["reactionCode"]);
  const targetStateCode = readNullableCode(reaction["targetStateCode"]);
  return {
    reactionCode: reactionCode.value,
    targetStateCode: targetStateCode.value,
    isMalformed: reactionCode.isMalformed || targetStateCode.isMalformed,
  };
}

function readOptionalCode(value: unknown): {
  readonly value: string | null;
  readonly isMalformed: boolean;
} {
  return typeof value === "string" && hasVisibleValue(value)
    ? { value: value.trim(), isMalformed: false }
    : { value: null, isMalformed: true };
}

function readDefaultedCode(
  value: unknown,
  defaultValue: string,
): { readonly value: string | null; readonly isMalformed: boolean } {
  return value === null || value === undefined
    ? { value: defaultValue, isMalformed: false }
    : readOptionalCode(value);
}

function readNullableCode(value: unknown): {
  readonly value: string | null;
  readonly isMalformed: boolean;
} {
  return value === null || value === undefined
    ? { value: null, isMalformed: false }
    : readOptionalCode(value);
}

function readStringCodeCollection(value: unknown): {
  readonly values: readonly string[];
  readonly isMalformed: boolean;
} {
  if (value === null || value === undefined) {
    return { values: [], isMalformed: false };
  }
  if (!Array.isArray(value)) {
    return { values: [], isMalformed: true };
  }

  const values = value
    .filter((entry): entry is string => typeof entry === "string" && hasVisibleValue(entry))
    .map((entry) => entry.trim());
  return {
    values: sortedCodes(uniqueVisibleCodes(values)),
    isMalformed: values.length !== value.length,
  };
}

function hasMalformedNotificationCollection(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (!Array.isArray(value)) {
    return true;
  }

  const notificationIds = new Set<string>();
  for (const entry of value) {
    const notification = asRecord(entry);
    if (!notification) {
      return true;
    }

    const id = readOptionalCode(notification["id"]);
    const recipientTypeCode = readOptionalCode(notification["recipientTypeCode"]);
    const recipientTargetKey = readNullableCode(notification["recipientTargetKey"]);
    const severityCode = readOptionalCode(notification["severityCode"]);
    if (
      id.isMalformed
      || recipientTypeCode.isMalformed
      || recipientTargetKey.isMalformed
      || severityCode.isMalformed
      || !hasVisibleValue(id.value)
      || !hasVisibleValue(recipientTypeCode.value)
    ) {
      return true;
    }
    if (notificationIds.has(id.value)) {
      return true;
    }
    notificationIds.add(id.value);

    const normalizedRecipientType = normalizeSemanticCode(recipientTypeCode.value);
    const hasTarget = hasVisibleValue(recipientTargetKey.value);
    if (
      NOTIFICATION_TARGET_REQUIRED_RECIPIENT_TYPES.has(normalizedRecipientType) !== hasTarget
      && (
        NOTIFICATION_TARGET_REQUIRED_RECIPIENT_TYPES.has(normalizedRecipientType)
        || NOTIFICATION_DERIVED_TARGET_RECIPIENT_TYPES.has(normalizedRecipientType)
      )
    ) {
      return true;
    }
  }

  return false;
}

function uniqueVisibleCodes(values: readonly (string | null)[]): string[] {
  const valuesByKey = new Map<string, string>();
  values.forEach((value) => {
    if (!hasVisibleValue(value)) {
      return;
    }
    const normalized = normalizeSemanticCode(value);
    if (!valuesByKey.has(normalized)) {
      valuesByKey.set(normalized, value.trim());
    }
  });
  return [...valuesByKey.values()];
}

function sortedCodes(values: readonly string[]): string[] {
  return [...values].sort((left, right) =>
    compareText(normalizeSemanticCode(left), normalizeSemanticCode(right)));
}

function sentenceCase(value: string): string {
  return value.length === 0
    ? value
    : `${value[0].toLocaleUpperCase("en-US")}${value.slice(1)}`;
}

function uniqueTexts(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function normalizeSemanticCode(value: string | null | undefined): string {
  return value?.trim().toLocaleLowerCase("en-US") ?? "";
}

function countTransitions(value: unknown): TransitionCountResult {
  const entries = arrayValue(value);
  let applied = 0;
  let malformed = 0;
  let appliedDecisions = 0;
  let malformedDecisions = 0;

  entries.forEach((entry) => {
    const transition = asRecord(entry);
    if (!transition) {
      malformed += 1;
      return;
    }

    applied += 1;
    const structuredDecisions = countDecisions(transition["requiredDecisions"]);
    const legacyDecisions = countStringEntries(transition["requiredDecisionKinds"]);
    appliedDecisions += structuredDecisions.applied + legacyDecisions.applied;
    malformedDecisions += structuredDecisions.malformed + legacyDecisions.malformed;
    malformed += collectionShapeErrorCount(transition["requiredDecisions"])
      + collectionShapeErrorCount(transition["requiredDecisionKinds"]);
  });

  return {
    applied,
    malformed,
    decisions: {
      applied: appliedDecisions,
      malformed: malformedDecisions,
    },
  };
}

function countDecisions(value: unknown): CountResult {
  return countRecordEntries(value);
}

function countStringEntries(value: unknown): CountResult {
  const entries = arrayValue(value);
  return entries.reduce<CountResult>((counts, entry) => {
    if (typeof entry === "string" && hasVisibleValue(entry)) {
      return { applied: counts.applied + 1, malformed: counts.malformed };
    }

    return { applied: counts.applied, malformed: counts.malformed + 1 };
  }, { applied: 0, malformed: 0 });
}

function countRecordEntries(value: unknown): CountResult {
  const entries = arrayValue(value);
  return entries.reduce<CountResult>((counts, entry) => {
    if (asRecord(entry)) {
      return { applied: counts.applied + 1, malformed: counts.malformed };
    }

    return { applied: counts.applied, malformed: counts.malformed + 1 };
  }, { applied: 0, malformed: 0 });
}

function toNodeKind(typeCode: string): WorkflowReadOnlyGraphNodeKind {
  switch (typeCode) {
    case "entry":
      return "entry";
    case "human_step":
      return "human-step";
    case "automated_step":
      return "automated-step";
    case "routing_decision":
      return "routing-decision";
    case "terminal":
      return "terminal";
    default:
      return "invalid";
  }
}

function toNodeGroupLabel(kind: WorkflowReadOnlyGraphNodeKind): string {
  switch (kind) {
    case "entry":
      return "Entry";
    case "human-step":
      return "Human step";
    case "automated-step":
      return "Automated step";
    case "routing-decision":
      return "Routing decision";
    case "terminal":
      return "Terminal";
    case "missing-endpoint":
      return "Missing endpoint";
    default:
      return "Invalid node";
  }
}

function buildNodeLabel(
  node: ProjectedNodeRecord,
  kind: WorkflowReadOnlyGraphNodeKind,
): string {
  if (hasVisibleValue(node.displayName)) {
    return node.displayName.trim();
  }
  if (["human-step", "automated-step"].includes(kind) && hasVisibleValue(node.stepCode)) {
    return formatCodeLabel(node.stepCode);
  }
  if (kind !== "invalid") {
    return toNodeGroupLabel(kind);
  }
  if (hasVisibleValue(node.sourceId)) {
    return formatCodeLabel(node.sourceId);
  }

  return `Invalid node ${node.index + 1}`;
}

function buildGraphDescription(
  nodeCount: number,
  edgeCount: number,
  transitionCount: number,
  decisionCount: number,
  invalidElementCount: number,
  sourceContext: WorkflowGraphProjectionSourceContext,
): string {
  const sourceLabel = sourceContext === "published-version" ? "saved" : "draft";
  const summary = `${nodeCount} node${pluralSuffix(nodeCount)}, ${edgeCount} route${pluralSuffix(edgeCount)}, ${transitionCount} ${sourceLabel} transition ${entryWord(transitionCount)}, and ${decisionCount} ${sourceLabel} decision ${entryWord(decisionCount)}.`;
  return invalidElementCount > 0
    ? sourceContext === "published-version"
      ? `${summary} ${invalidElementCount} invalid graph element${pluralSuffix(invalidElementCount)} remain visible as immutable saved evidence.`
      : `${summary} ${invalidElementCount} graph element${pluralSuffix(invalidElementCount)} require correction.`
    : summary;
}

function findDuplicates(values: readonly string[]): ReadonlySet<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  values.forEach((value) => {
    if (seen.has(value)) {
      duplicates.add(value);
    }
    seen.add(value);
  });
  return duplicates;
}

function compareNodeRecords(left: ProjectedNodeRecord, right: ProjectedNodeRecord): number {
  const leftKind = toNodeKind(left.typeCode);
  const rightKind = toNodeKind(right.typeCode);
  return compareNumber(left.displayOrder ?? Number.MAX_SAFE_INTEGER, right.displayOrder ?? Number.MAX_SAFE_INTEGER)
    || compareNumber(NODE_KIND_ORDER[leftKind], NODE_KIND_ORDER[rightKind])
    || compareText(left.sourceId, right.sourceId)
    || compareText(left.stepCode ?? "", right.stepCode ?? "")
    || compareText(left.displayName ?? "", right.displayName ?? "")
    || compareNumber(left.index, right.index);
}

function compareEdgeRecords(left: ProjectedEdgeRecord, right: ProjectedEdgeRecord): number {
  return compareText(left.sourceId, right.sourceId)
    || compareText(left.fromSourceNodeId, right.fromSourceNodeId)
    || compareText(left.toSourceNodeId, right.toSourceNodeId)
    || compareNumber(left.index, right.index);
}

function compareProjectedEdges(
  left: ProjectedEdgeRecord,
  right: ProjectedEdgeRecord,
  linearChain: LinearChainProjection | null,
): number {
  if (!linearChain) {
    return compareEdgeRecords(left, right);
  }

  return compareNumber(
    linearChain.edgeOrderBySourceIndex.get(left.index) ?? Number.MAX_SAFE_INTEGER,
    linearChain.edgeOrderBySourceIndex.get(right.index) ?? Number.MAX_SAFE_INTEGER,
  ) || compareEdgeRecords(left, right);
}

function projectLinearChain(
  nodes: readonly ProjectedNodeRecord[],
  edges: readonly ProjectedEdgeRecord[],
  duplicateNodeIds: ReadonlySet<string>,
  duplicateEdgeIds: ReadonlySet<string>,
): LinearChainProjection | null {
  if (
    nodes.length < 2
    || edges.length !== nodes.length - 1
    || duplicateNodeIds.size > 0
    || duplicateEdgeIds.size > 0
    || nodes.some((node) => !isValidChainNode(node))
    || edges.some((edge) => !isValidChainEdge(edge))
  ) {
    return null;
  }

  const nodesById = new Map(nodes.map((node) => [node.sourceId, node]));
  const entryNodes = nodes.filter((node) => toNodeKind(node.typeCode) === "entry");
  const terminalNodes = nodes.filter((node) => toNodeKind(node.typeCode) === "terminal");
  if (entryNodes.length !== 1 || terminalNodes.length !== 1) {
    return null;
  }

  const incomingByNodeId = new Map(nodes.map((node) => [node.sourceId, [] as ProjectedEdgeRecord[]]));
  const outgoingByNodeId = new Map(nodes.map((node) => [node.sourceId, [] as ProjectedEdgeRecord[]]));
  for (const edge of edges) {
    if (
      edge.fromSourceNodeId === edge.toSourceNodeId
      || !nodesById.has(edge.fromSourceNodeId)
      || !nodesById.has(edge.toSourceNodeId)
    ) {
      return null;
    }

    outgoingByNodeId.get(edge.fromSourceNodeId)!.push(edge);
    incomingByNodeId.get(edge.toSourceNodeId)!.push(edge);
  }

  for (const node of nodes) {
    const kind = toNodeKind(node.typeCode);
    const incomingCount = incomingByNodeId.get(node.sourceId)!.length;
    const outgoingCount = outgoingByNodeId.get(node.sourceId)!.length;
    const hasExpectedDegree = kind === "entry"
      ? incomingCount === 0 && outgoingCount === 1
      : kind === "terminal"
        ? incomingCount === 1 && outgoingCount === 0
        : incomingCount === 1 && outgoingCount === 1;
    if (!hasExpectedDegree) {
      return null;
    }
  }

  const nodeProcessOrderBySourceIndex = new Map<number, number>();
  const edgeOrderBySourceIndex = new Map<number, number>();
  const visitedNodeIds = new Set<string>();
  let humanStepOrder = 0;
  let current = entryNodes[0];

  while (true) {
    if (visitedNodeIds.has(current.sourceId)) {
      return null;
    }
    visitedNodeIds.add(current.sourceId);

    if (toNodeKind(current.typeCode) === "human-step") {
      humanStepOrder += 1;
      nodeProcessOrderBySourceIndex.set(current.index, humanStepOrder);
    }

    const outgoing = outgoingByNodeId.get(current.sourceId)!;
    if (outgoing.length === 0) {
      break;
    }

    const edge = outgoing[0];
    edgeOrderBySourceIndex.set(edge.index, edgeOrderBySourceIndex.size);
    current = nodesById.get(edge.toSourceNodeId)!;
  }

  if (
    current.sourceId !== terminalNodes[0].sourceId
    || visitedNodeIds.size !== nodes.length
    || edgeOrderBySourceIndex.size !== edges.length
  ) {
    return null;
  }

  return { nodeProcessOrderBySourceIndex, edgeOrderBySourceIndex };
}

function isValidChainNode(node: ProjectedNodeRecord): boolean {
  const kind = toNodeKind(node.typeCode);
  return hasVisibleValue(node.sourceId)
    && kind !== "invalid"
    && (!["human-step", "automated-step"].includes(kind) || hasVisibleValue(node.stepCode));
}

function isValidChainEdge(edge: ProjectedEdgeRecord): boolean {
  return !edge.hasMalformedShape
    && hasVisibleValue(edge.sourceId)
    && hasVisibleValue(edge.fromSourceNodeId)
    && hasVisibleValue(edge.toSourceNodeId);
}

function compareGraphNodes(left: WorkflowReadOnlyGraphNode, right: WorkflowReadOnlyGraphNode): number {
  return compareNumber(NODE_KIND_ORDER[left.kind], NODE_KIND_ORDER[right.kind])
    || compareText(left.sourceId, right.sourceId)
    || compareText(left.id, right.id);
}

function indexedArray(value: unknown): IndexedValue[] {
  return arrayValue(value).map((entry, index) => ({ index, value: entry }));
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function collectionShapeErrorCount(value: unknown): number {
  return value === null || value === undefined || Array.isArray(value) ? 0 : 1;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readNullableString(value: unknown): string | null {
  return value === null || value === undefined
    ? null
    : typeof value === "string"
      ? value
      : null;
}

function hasVisibleValue(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function displayValue(value: string | null | undefined): string {
  return hasVisibleValue(value) ? value : "Not provided";
}

function formatCodeLabel(value: string | null | undefined): string {
  if (!hasVisibleValue(value)) {
    return "";
  }

  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function visualizationId(prefix: string, sourceId: string, occurrence: number): string {
  const encodedSourceId = encodeURIComponent(hasVisibleValue(sourceId) ? sourceId : "not-provided");
  return `${prefix}:${encodedSourceId}:${occurrence}`;
}

function nextOccurrence(occurrences: Map<string, number>, sourceId: string): number {
  const key = hasVisibleValue(sourceId) ? sourceId : "";
  const occurrence = (occurrences.get(key) ?? 0) + 1;
  occurrences.set(key, occurrence);
  return occurrence;
}

function compareText(left: string, right: string): number {
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

function compareNumber(left: number, right: number): number {
  return left - right;
}

function pluralSuffix(count: number): string {
  return count === 1 ? "" : "s";
}

function entryWord(count: number): string {
  return count === 1 ? "entry" : "entries";
}
